// src/screens/s3/VoyantConfirm.tsx
// ─────────────────────────────────────────────────────────────────────────────
// Session 3 — Confirm Movement
// Emotional peak — sparse layout with 5 key read-back fields
// ─────────────────────────────────────────────────────────────────────────────

import React from 'react';
import { ClientData } from '../../types/ClientData';
import { formatCurrency } from '../../utils/format';

interface Props {
  data: ClientData;
  onUpdate: (updates: Partial<ClientData>) => void;
}

export default function VoyantConfirm({ data, onUpdate }: Props) {
  const allFieldsFilled =
    data.s3FinalFreedomAge > 0 &&
    data.s3FinalPotAtFreedom > 0 &&
    data.s3FinalMonthlyDrawdown > 0 &&
    data.s3FinalFundedToAge > 0 &&
    data.s3FinalStressResult !== '';

  return (
    <div className="max-w-2xl mx-auto py-12 px-6">
      <div className="text-center mb-12">
        <p className="text-xs text-gray-500 uppercase tracking-wider font-medium mb-2">
          Adviser Instruction
        </p>
        <p className="text-sm text-gray-600">
          Populate the five fields below with final figures from Voyant.
        </p>
      </div>

      {/* Five read-back fields in a clean grid */}
      <div className="space-y-6 mb-12">
        {/* Field 1: Freedom Age */}
        <div>
          <label className="text-xs text-gray-400 uppercase tracking-wide font-medium block mb-2">
            Final Freedom Age
          </label>
          <input
            type="number"
            min="55"
            max="75"
            value={data.s3FinalFreedomAge || ''}
            onChange={(e) => onUpdate({ s3FinalFreedomAge: e.target.value === '' ? 0 : Number(e.target.value) })}
            className="text-4xl font-bold text-ma-navy bg-gray-50 border border-gray-200 rounded-lg px-6 py-4 w-full focus:outline-none focus:ring-2 focus:ring-ma-navy/20"
          />
        </div>

        {/* Field 2: Pot at Freedom */}
        <div>
          <label className="text-xs text-gray-400 uppercase tracking-wide font-medium block mb-2">
            Portfolio Value at Freedom
          </label>
          <div className="flex items-baseline gap-2">
            <span className="text-gray-400">£</span>
            <input
              type="number"
              min="0"
              step="10000"
              value={data.s3FinalPotAtFreedom || ''}
              onChange={(e) => onUpdate({ s3FinalPotAtFreedom: e.target.value === '' ? 0 : Number(e.target.value) })}
              className="text-4xl font-bold text-ma-navy bg-gray-50 border border-gray-200 rounded-lg px-6 py-4 flex-1 focus:outline-none focus:ring-2 focus:ring-ma-navy/20"
            />
          </div>
          {data.s3FinalPotAtFreedom > 0 && (
            <p className="text-xs text-gray-500 mt-1">{formatCurrency(data.s3FinalPotAtFreedom)}</p>
          )}
        </div>

        {/* Field 3: Monthly Drawdown */}
        <div>
          <label className="text-xs text-gray-400 uppercase tracking-wide font-medium block mb-2">
            Monthly Retirement Income
          </label>
          <div className="flex items-baseline gap-2">
            <span className="text-gray-400">£</span>
            <input
              type="number"
              min="0"
              step="100"
              value={data.s3FinalMonthlyDrawdown || ''}
              onChange={(e) => onUpdate({ s3FinalMonthlyDrawdown: e.target.value === '' ? 0 : Number(e.target.value) })}
              className="text-4xl font-bold text-ma-navy bg-gray-50 border border-gray-200 rounded-lg px-6 py-4 flex-1 focus:outline-none focus:ring-2 focus:ring-ma-navy/20"
            />
          </div>
          {data.s3FinalMonthlyDrawdown > 0 && (
            <p className="text-xs text-gray-500 mt-1">
              {formatCurrency(data.s3FinalMonthlyDrawdown)}/month
            </p>
          )}
        </div>

        {/* Field 4: Funded To Age */}
        <div>
          <label className="text-xs text-gray-400 uppercase tracking-wide font-medium block mb-2">
            Plan Funded To Age
          </label>
          <input
            type="number"
            min="85"
            max="100"
            value={data.s3FinalFundedToAge || ''}
            onChange={(e) => onUpdate({ s3FinalFundedToAge: e.target.value === '' ? 0 : Number(e.target.value) })}
            className="text-4xl font-bold text-ma-navy bg-gray-50 border border-gray-200 rounded-lg px-6 py-4 w-full focus:outline-none focus:ring-2 focus:ring-ma-navy/20"
          />
        </div>

        {/* Field 5: Stress Test Result */}
        <div>
          <label className="text-xs text-gray-400 uppercase tracking-wide font-medium block mb-2">
            Stress Test Result (4% Growth)
          </label>
          <select
            value={data.s3FinalStressResult}
            onChange={(e) => onUpdate({ s3FinalStressResult: e.target.value as 'funded' | 'shortfall' | '' })}
            className="w-full text-lg font-bold text-ma-navy bg-gray-50 border border-gray-200 rounded-lg px-6 py-4 focus:outline-none focus:ring-2 focus:ring-ma-navy/20"
          >
            <option value="">Select test result...</option>
            <option value="funded">Funded ✓</option>
            <option value="shortfall">Shortfall ⚠</option>
          </select>
        </div>
      </div>

      {/* Confirmation Statement */}
      {allFieldsFilled && (
        <div className="bg-ma-navy text-white rounded-2xl p-8 text-center">
          <p className="text-sm font-medium uppercase tracking-wide mb-4">Plan Confirmed</p>
          <p className="text-lg leading-relaxed">
            Your plan is set to deliver freedom at age {data.s3FinalFreedomAge}, with a portfolio of {formatCurrency(data.s3FinalPotAtFreedom)} providing {formatCurrency(data.s3FinalMonthlyDrawdown)}/month in retirement. You're funded to age {data.s3FinalFundedToAge}, and the plan remains robust even if returns are lower than expected.
          </p>
        </div>
      )}
    </div>
  );
}
